import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { NoteFormFields } from '@/features/notes/components/note-form-fields';
import { useFormAutoSave } from '@/lib/hooks/use-form-auto-save';
import type { CrmNoteDTO } from '@/server/dto/crm-note-dto';
import type { CrmNoteInput } from '@/server/dto/crm-note-input-dto';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

interface NoteDetailsFormProps {
    note: CrmNoteDTO;
    onUpdate?: (values: CrmNoteInput) => void;
}

export const EventNoteForm = ({ note: noteDetails, onUpdate }: NoteDetailsFormProps) => {
    const sendEvent = useAnalytics();

    const { control, reset, watch, formState } = useForm<CrmNoteInput>({
        mode: 'onChange',
        defaultValues: {
            title: noteDetails.titleRich,
        },
    });

    useEffect(() => {
        sendEvent('customer_interaction_viewed', {
            note_id: noteDetails.noteId,
            customer_id: noteDetails.customerId ?? 'unknown',
            note_type: noteDetails.noteType,
        });
    }, []);

    // Update form with data when it's loaded
    useEffect(() => {
        reset(
            {
                title: noteDetails.titleRich,
            },
            {
                keepValues: true,
            },
        );
    }, [noteDetails, reset]);

    // Important that we read state here, otherwise the variable will not be updated
    const isFormValid = formState.isValid;

    const handleAutoSave = (values: CrmNoteInput) => {
        if (noteDetails && isFormValid) {
            onUpdate?.(values as CrmNoteInput);
        }
    };

    const autoSaveHandlerRef = useRef(handleAutoSave);
    autoSaveHandlerRef.current = handleAutoSave;

    // Auto-save when form values change and the form is dirty
    useFormAutoSave(
        watch,
        (values) => {
            autoSaveHandlerRef.current(values);
        },
        1000,
    );

    return (
        <NoteFormFields
            noteType={noteDetails.noteType}
            noteId={noteDetails.noteId}
            control={control}
            errors={formState.errors}
        />
    );
};
